import { FC, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMedplum, Panel } from '@medplum/react';
import { Patient } from '@medplum/fhirtypes';
import { Paper, Stack, Text, Button, Group, Loader } from '@mantine/core';
import { IconPlus, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCurrentEncounter } from '@hooks/useCurrentEncounter';
import { EncounterModal } from '../encounters-tab/EncounterModal';
import { CurrentEncounterTabs } from './CurrentEncounterTabs';

export const CurrentEncounterTab: FC = () => {
  const { patientId } = useParams();
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);
  const [patient, setPatient] = useState<Patient>();
  const [loading, setLoading] = useState(true);
  const [encounterModalOpened, setEncounterModalOpened] = useState(false);

  // Fetch patient data
  useEffect(() => {
    if (patientId) {
      medplum
        .readResource('Patient', patientId)
        .then((fetchedPatient) => {
          setPatient(fetchedPatient);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching patient:', error);
          setLoading(false);
        });
    }
  }, [medplum, patientId]);

  const { currentEncounter, setCurrentEncounter } =
    useCurrentEncounter(patient);

  if (loading || !patient) {
    return (
      <Panel mx="lg">
        <Group justify="center" p="xl">
          <Loader />
        </Group>
      </Panel>
    );
  }

  // Show "No Encounter in Progress" banner
  if (!currentEncounter) {
    return (
      <>
        <Panel mx="lg">
          <Paper p="xl" withBorder radius="md" bg="blue.0">
            <Stack gap="md" align="center">
              <IconInfoCircle size={48} color="#228be6" />
              <Text size="xl" fw={600} ta="center">
                {t('currentEncounter.noEncounterTitle', {
                  ns: 'patient-pages',
                })}
              </Text>
              <Text size="md" c="dimmed" ta="center" maw={500}>
                {t('currentEncounter.noEncounterMessage', {
                  ns: 'patient-pages',
                })}
              </Text>
              <Group justify="center" mt="md">
                <Button
                  leftSection={<IconPlus size={16} />}
                  size="md"
                  onClick={() => setEncounterModalOpened(true)}
                >
                  {t('currentEncounter.createEncounter', {
                    ns: 'patient-pages',
                  })}
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Panel>

        {/* Encounter Creation Modal */}
        <EncounterModal
          opened={encounterModalOpened}
          onClose={() => setEncounterModalOpened(false)}
          onEncounterCreated={(encounter) => {
            setCurrentEncounter(encounter);
            setEncounterModalOpened(false);
          }}
          patient={patient}
        />
      </>
    );
  }

  // Show tabbed workspace when an encounter exists
  return (
    <CurrentEncounterTabs patient={patient} encounter={currentEncounter} />
  );
};
